import { getAuthHeader } from 'app/selectors';
import { DEFAULT_META_KEY } from 'utils/constants';
import { createBoundActions } from 'utils/createBoundActions';

import type { PayloadAction } from '@reduxjs/toolkit';
import { request } from 'graphql-request';
import { call, put, takeEvery, select, all, cancel, cancelled, fork, take } from 'redux-saga/effects';

import { requestSlice, RequestStatus } from './requestSlice';

type GqlRequest = {
    query: string;
    variables?: Record<any, any>;
};

const getHeaders = (authHeader: string) => {
    // eslint-disable-next-line compat/compat
    const requestHeaders: HeadersInit = new Headers();

    requestHeaders.set('Authorization', authHeader);

    return requestHeaders;
};

const getServiceUrl = (): string =>
    (window as any).app.env.REACT_APP_GRAPHQL_APIURL || process.env.REACT_APP_GRAPHQL_APIURL || '/api';

export function* watchMakeRequest(action: PayloadAction<GqlRequest, any, any>) {
    // eslint-disable-next-line compat/compat
    const abortController = new AbortController();
    const { query, variables } = action.payload;
    const instanceId = action.meta?.[DEFAULT_META_KEY];

    const { setStatus, setData, setError, setRequestCode, setVariables } = instanceId
        ? createBoundActions(requestSlice.actions, instanceId)
        : requestSlice.actions;

    const header: string = yield select(getAuthHeader);

    yield put(setStatus(RequestStatus.FETCHING));
    yield put(setVariables(variables));

    try {
        const options = {
            url: getServiceUrl(),
            document: query,
            variables,
            requestHeaders: getHeaders(header),
            signal: abortController.signal,
        };

        // @ts-ignore
        const response = yield call(request, options);

        yield put(setData(response));
        yield put(setStatus(RequestStatus.SUCCESS));
    } catch (e: any) {
        yield put(setRequestCode(e?.response?.status));

        if (e?.response?.errors) {
            yield put(setError(JSON.stringify(e?.response?.errors)));
        } else {
            yield put(setError(JSON.stringify(e?.response)));
        }

        yield put(setStatus(RequestStatus.ERROR));
    } finally {
        // @ts-ignore
        if (yield cancelled()) {
            abortController.abort();
            yield put(setStatus(RequestStatus.ERROR));
        }
    }
}

// @ts-ignore
function* stopUploadMediaWorker(action) {
    yield cancel(action);
}

export function* watchMakeResetStoreData(action: PayloadAction<{ value: any }, any, any>) {
    const instanceId = action.meta?.[DEFAULT_META_KEY];
    const { value: newState } = action.payload;

    const { setStatus, setData, setError, setRequestCode } = createBoundActions(requestSlice.actions, instanceId);

    yield all([
        put(setStatus(newState)),
        put(setData(newState)),
        put(setError(newState)),
        put(setRequestCode(newState)),
    ]);
}

export function* requestSaga() {
    while (true) {
        // @ts-ignore
        const forkedTask = yield fork(watchMakeRequest, yield take(requestSlice.actions.makeRequest));

        yield takeEvery(requestSlice.actions.abortRequest, stopUploadMediaWorker, forkedTask);
        yield takeEvery(requestSlice.actions.resetStoreData, watchMakeResetStoreData);
    }
}
